import {useQuery} from "@tanstack/react-query";
import {Button, Modal, Paper, TextInput} from "@mantine/core";
import { MagnifyingGlass as IconSearch, TreeStructure as IconSitemap } from "@phosphor-icons/react";
import {DataTable} from "mantine-datatable";
import {useMemo, useState} from "react";
import {Row, Col} from "../../components/Flex/Flex";
import LoaderScreen from "../../components/Loader/LoaderScreen";
import Page from "../../components/Page/Page";
import api from "../../utils/api";

const fetchFactions = async () => {
    const {data} = await api.get("/admin/factions");
    return data;
}

const PAGE_SIZE = 50;

export default function FactionsPage() {
    const {data: factions, isPending} = useQuery({
        queryKey: ['admin-factions-list'],
        queryFn: fetchFactions,
    });
    const [globalFilter, setGlobalFilter] = useState('');
    const [page, setPage] = useState(1);

    const [permDialog, setPermDialog] = useState({visible: false, faction: null});

    const filteredFactions = useMemo(() => {
        if (!factions) return [];
        const q = globalFilter.trim().toLowerCase();
        if (q.length === 0) return factions;
        return factions.filter(f =>
            [String(f.id ?? ''), String(f.name ?? ''), String(f.leader?.name ?? ''), String(f.second?.name ?? '')]
                .some(v => v.toLowerCase().includes(q))
        );
    }, [factions, globalFilter]);

    const paginatedFactions = useMemo(() => {
        return filteredFactions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    }, [filteredFactions, page]);

    if (isPending || !factions) {
        return <LoaderScreen message="Loading factions..."/>
    }

    const logoBody = (row) => <img src={row.logo} alt="logo" width={50} height={50} style={{objectFit: 'contain'}}/>;
    const nameBody = (row) => (
        <span>
            {row.name}
            {row.parent !== null ? <IconSitemap size={14} style={{marginLeft: 6}} title="Subfaction"/> : null}
        </span>
    );

    const permissionsCountBody = (row) => {
        const count = row?.permissions?.length || 0;
        return (
            <Button variant="subtle" onClick={() => setPermDialog({visible: true, faction: row})}>{count}</Button>
        );
    };

    const permYesNo = (value) => value ? 'Yes' : '';

    function FactionPermissionsModal({visible, faction, onHide}) {
        return (
            <Modal opened={visible} onClose={onHide} title={faction ? `Permissions for ${faction.name}` : 'Permissions'} size="40rem">
                {faction && (
                    <DataTable
                        records={faction.permissions || []}
                        noRecordsText="No permissioned users"
                        idAccessor="user"
                        columns={[
                            {accessor: '_index', title: '#', render: (_, index) => index + 1, width: 60},
                            {accessor: 'userName', title: 'User'},
                            {accessor: 'manageTavernNetwork', title: 'Network', render: (row) => permYesNo(row.manageTavernNetwork), width: 120},
                            {accessor: 'manageLicenses', title: 'Licenses', render: (row) => permYesNo(row.manageLicenses), width: 120},
                            {accessor: 'managePermissions', title: 'Permissions', render: (row) => permYesNo(row.managePermissions), width: 130},
                        ]}
                    />
                )}
            </Modal>
        );
    }

    return (
        <Page>
            <title>Admin - Factions</title>
            <Paper p="md">
                <Row>
                    <Col grow={1}>
                        <h2 className="m-0">Factions</h2>
                    </Col>
                    <Col>
                        <TextInput
                            leftSection={<IconSearch size={16}/>}
                            value={globalFilter}
                            onChange={e => { setGlobalFilter(e.target.value); setPage(1); }}
                            placeholder="Search"
                            style={{marginBottom: '0.5rem'}}
                        />
                    </Col>
                </Row>
                <DataTable
                    records={paginatedFactions}
                    totalRecords={filteredFactions.length}
                    recordsPerPage={PAGE_SIZE}
                    page={page}
                    onPageChange={setPage}
                    columns={[
                        {accessor: 'logo', title: 'Logo', render: (row) => logoBody(row), width: 80},
                        {accessor: 'id', title: 'ID', cellsClassName: 'text-xs'},
                        {accessor: 'name', title: 'Name', render: (row) => nameBody(row)},
                        {accessor: 'leader.name', title: 'Leader'},
                        {accessor: 'second.name', title: 'Second'},
                        {accessor: 'permissions', title: '# Perm. Members', render: (row) => permissionsCountBody(row), width: 150},
                    ]}
                />
            </Paper>

            <FactionPermissionsModal
                visible={permDialog.visible}
                faction={permDialog.faction}
                onHide={() => setPermDialog({visible: false, faction: null})}
            />
        </Page>
    )
}
